/**
 * Sell / Buyback page architecture sections, uBreakiFix-style.
 * Compose this anywhere as <SellArchitectureBlock locality="Chicago" brand="iPhone" />.
 * Pure-additive: it does NOT modify existing sell page content. Drop into any sell page.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Phone, MapPin, Clock, Star, ChevronRight, ArrowRight, Smartphone, Laptop, Tablet,
  Gamepad2, Headphones, Store, Package, Truck, Boxes, DollarSign, Banknote, ShieldCheck,
  CheckCircle, Zap, ThumbsUp, Wallet, Navigation, Award, TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { BUSINESS } from '../data/seoPagesData';
import { GOOGLE_BUSINESS } from '../config/googleBusiness';

// ── Helpers ──────────────────────────────────────────────────────────────────
const getOpenStatus = () => {
  const now = new Date();
  const day = now.getDay();
  const h = now.getHours() + now.getMinutes() / 60;
  if (day === 0) return { open: false, label: 'Closed', next: 'Opens Mon at 10AM' };
  if (day === 6) {
    if (h >= 10 && h < 16) return { open: true, label: 'Open', next: 'Closes at 4PM' };
    return { open: false, label: 'Closed', next: h < 10 ? 'Opens at 10AM' : 'Opens Mon at 10AM' };
  }
  if (h >= 10 && h < 18) return { open: true, label: 'Open', next: 'Closes at 6PM' };
  return { open: false, label: 'Closed', next: h < 10 ? 'Opens at 10AM' : 'Opens tomorrow at 10AM' };
};

const NEIGHBORHOODS_FOR_SELL = [
  { name: 'Logan Square', slug: 'logan-square', miles: 2.4 },
  { name: 'Avondale', slug: 'avondale', miles: 1.5 },
  { name: 'Portage Park', slug: 'portage-park', miles: 1.2 },
  { name: 'Albany Park', slug: 'albany-park', miles: 1.8 },
  { name: 'Lincoln Square', slug: 'lincoln-square', miles: 2.9 },
  { name: 'Jefferson Park', slug: 'jefferson-park', miles: 2.1 },
  { name: 'North Center', slug: 'north-center', miles: 2.7 },
  { name: 'Wicker Park', slug: 'wicker-park', miles: 3.9 },
  { name: 'Lincoln Park', slug: 'lincoln-park', miles: 4.4 },
  { name: 'Evanston', slug: 'evanston', miles: 7.4 },
];

// ── Store info bar (compact white bar, for pages with their own hero) ───────
export const SellStoreInfoBar = () => {
  const [status, setStatus] = useState(getOpenStatus());
  useEffect(() => {
    const t = setInterval(() => setStatus(getOpenStatus()), 60_000);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="bg-white border-y border-slate-200" data-testid="sell-store-info-bar">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 grid grid-cols-2 lg:grid-cols-4 gap-3 items-center">
        <a
          href={GOOGLE_BUSINESS.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:text-emerald-600 text-slate-700"
          data-testid="sell-bar-rating"
        >
          <div className="flex">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
          </div>
          <span className="text-sm font-semibold">{BUSINESS.rating}</span>
          <span className="text-xs text-slate-500">({BUSINESS.reviewCount}+)</span>
        </a>
        <div className={`flex items-center gap-2 text-sm font-semibold ${status.open ? 'text-emerald-600' : 'text-slate-500'}`} data-testid="sell-bar-status">
          <span className={`w-2 h-2 rounded-full ${status.open ? 'bg-emerald-500' : 'bg-slate-400'}`} />
          {status.label} <span className="text-slate-400 font-normal">· {status.next}</span>
        </div>
        <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-2 text-slate-700 hover:text-emerald-600 text-sm font-semibold" data-testid="sell-bar-phone">
          <Phone className="w-4 h-4 text-emerald-500" /> {BUSINESS.phone}
        </a>
        <a
          href={GOOGLE_BUSINESS.directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-slate-700 hover:text-emerald-600 text-sm font-semibold"
          data-testid="sell-bar-directions"
        >
          <Navigation className="w-4 h-4 text-emerald-500" /> {BUSINESS.address}
        </a>
      </div>
    </section>
  );
};

// ── 4 fulfillment cards (Sell flavor) ────────────────────────────────────────
export const SellFulfillmentCards = () => {
  const items = [
    {
      icon: Store,
      title: 'Walk in for cash',
      desc: 'Bring your device to our Irving Park shop. Cash in hand in 15 minutes.',
      cta: 'Get directions',
      href: GOOGLE_BUSINESS.directionsUrl,
      external: true,
    },
    {
      icon: Package,
      title: 'Ship it free',
      desc: 'Free prepaid UPS or USPS label. Insured up to $1,000. Payment same day device arrives.',
      cta: 'Start mail-in',
      href: '/sell',
    },
    {
      icon: Truck,
      title: 'Pickup service',
      desc: 'Within Chicago, we come to your home or office for 5+ devices. No fees.',
      cta: 'Request pickup',
      href: `tel:${BUSINESS.phoneRaw}`,
      external: true,
    },
    {
      icon: Boxes,
      title: 'Bulk / Corporate',
      desc: 'Selling 10+ devices? Custom quotes, NDA available, IT-friendly process.',
      cta: 'Get bulk quote',
      href: '/sell-multiple-devices',
    },
  ];
  return (
    <section className="bg-slate-50 border-y border-slate-200 py-12 sm:py-14" data-testid="sell-fulfillment-cards">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">Four easy ways to sell your device</h2>
        <p className="text-sm sm:text-base text-slate-600 mb-8 text-center max-w-2xl mx-auto">
          Walk in, ship it free, schedule a pickup, or send a bulk lot. Whatever works for you, we'll meet you there.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-300 hover:shadow-sm transition-all" data-testid={`sell-fulfillment-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-3">
                <it.icon className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-1 text-base">{it.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">{it.desc}</p>
              {it.external ? (
                <a href={it.href} target={it.href.startsWith('tel:') ? undefined : '_blank'} rel="noopener noreferrer" className="inline-flex items-center text-sm text-emerald-600 font-semibold hover:text-emerald-700">
                  {it.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </a>
              ) : (
                <Link to={it.href} className="inline-flex items-center text-sm text-emerald-600 font-semibold hover:text-emerald-700">
                  {it.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── What sellers can expect, 3 columns ──────────────────────────────────────
export const WhatToExpectSell = () => (
  <section className="bg-white py-12 sm:py-14" data-testid="what-to-expect-sell">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">Three things every seller can expect</h2>
      <p className="text-sm sm:text-base text-slate-600 mb-10 text-center max-w-2xl mx-auto">
        We're not Gazelle or Apple Trade-In, we pay more, faster, and in real cash.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
        {[
          { icon: TrendingUp, title: 'Higher payouts', desc: 'We typically pay more than Apple Trade-In, Gazelle, Decluttr, and BestBuy buyback.' },
          { icon: Zap, title: 'Same-day cash', desc: 'Walk in and walk out with cash, Zelle, or Venmo in 15 minutes. Mail-in is paid same day device arrives.' },
          { icon: ShieldCheck, title: 'No hidden fees', desc: 'Free shipping, free diagnostics, no restocking fees, no surprise downgrades. The quote you accept is what you get.' },
        ].map((it, i) => (
          <div key={i} className="text-center" data-testid={`sell-expect-${i}`}>
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <it.icon className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2 text-lg">{it.title}</h3>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{it.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ── Brands we buy strip (mirror of buy-side trust) ───────────────────────────
export const BrandsWeBuyStrip = () => (
  <section className="bg-slate-50 border-y border-slate-200 py-12 sm:py-14" data-testid="brands-we-buy-strip">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">The brands we buy at top dollar</h2>
        <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
          We pay competitive market value on every major manufacturer. Pick your brand to start a quote.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { brand: 'Apple', label: 'iPhone, MacBook, iPad', link: '/sell-iphone', range: 'Up to $950' },
          { brand: 'Samsung', label: 'Galaxy S, Note, Z Fold', link: '/sell-samsung', range: 'Up to $700' },
          { brand: 'Google', label: 'Pixel 6, 7, 8, 9', link: '/sell/phones-sell/google', range: 'Up to $450' },
          { brand: 'Microsoft', label: 'Surface, Xbox', link: '/sell/laptops-sell', range: 'Up to $850' },
        ].map((b, i) => (
          <Link
            key={i}
            to={b.link}
            className="group flex flex-col items-center justify-center text-center bg-white border border-slate-200 hover:border-emerald-300 rounded-xl px-4 py-6 transition-all hover:-translate-y-0.5"
            data-testid={`brand-we-buy-${b.brand.toLowerCase()}`}
          >
            <p className="text-xl sm:text-2xl font-bold text-slate-900 group-hover:text-emerald-700 mb-1 tracking-tight">{b.brand}</p>
            <p className="text-xs text-slate-500 group-hover:text-emerald-600 mb-2">{b.label}</p>
            <p className="text-xs font-semibold text-emerald-600">{b.range}</p>
          </Link>
        ))}
      </div>
      <p className="text-xs text-slate-500 text-center mt-6 max-w-3xl mx-auto leading-relaxed">
        Apple®, Samsung Galaxy®, Google Pixel®, and Microsoft Surface® are trademarks of their respective owners.
        2A Electronics Service is an independent buyer and is not affiliated with these manufacturers.
      </p>
    </div>
  </section>
);

// ── Device conditions we accept ──────────────────────────────────────────────
export const ConditionsWeBuy = () => (
  <section className="bg-white py-12 sm:py-14" data-testid="conditions-we-buy">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">We buy your device in good working condition</h2>
      <p className="text-sm sm:text-base text-slate-600 mb-8 text-center max-w-2xl mx-auto">
        Flawless, excellent, good, or fair, if it powers on and works, it has cash value at 2A.
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: 'Like new', desc: 'Top payout. Pristine devices in original packaging fetch the highest prices.', icon: CheckCircle, color: 'emerald' },
          { title: 'Excellent', desc: 'Light use, no damage. Works perfectly and looks great, near-top payout.', icon: Smartphone, color: 'emerald' },
          { title: 'Good', desc: 'Normal wear and minor scratches. Fully functional, strong cash offer.', icon: ShieldCheck, color: 'blue' },
          { title: 'Fair', desc: 'Heavier cosmetic wear but powers on and works normally, still worth cash.', icon: DollarSign, color: 'amber' },
        ].map((it, i) => (
          <Link
            key={i}
            to="/sell"
            className="group flex flex-col items-start gap-3 p-5 bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 rounded-xl transition-all"
            data-testid={`condition-card-${i}`}
          >
            <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 group-hover:border-emerald-200 flex items-center justify-center">
              <it.icon className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-base mb-1 group-hover:text-emerald-700">{it.title}</h3>
              <p className="text-xs sm:text-sm text-slate-600">{it.desc}</p>
            </div>
            <span className="inline-flex items-center text-xs font-semibold text-emerald-600 mt-1">
              Get quote <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// ── Most popular devices we buy, rich-anchor internal link grid ─────────────
export const MostPopularDevicesWeBuy = () => {
  const groups = [
    {
      heading: 'Most popular phones we buy',
      items: [
        { label: 'Sell iPhone 16 Pro Max', link: '/sell-my/iphone/iphone-16-pro-max' },
        { label: 'Sell iPhone 16 Pro', link: '/sell-my/iphone/iphone-16-pro' },
        { label: 'Sell iPhone 15 Pro Max', link: '/sell-my/iphone/iphone-15-pro-max' },
        { label: 'Sell iPhone 14', link: '/sell-my/iphone/iphone-14' },
        { label: 'Sell iPhone 13', link: '/sell-my/iphone/iphone-13' },
        { label: 'Sell Samsung Galaxy S24 Ultra', link: '/sell-my/samsung/galaxy-s24-ultra' },
      ],
    },
    {
      heading: 'Most popular laptops we buy',
      items: [
        { label: 'Sell MacBook Pro M3', link: '/sell-my/macbook/macbook-pro-m3' },
        { label: 'Sell MacBook Air M2', link: '/sell-my/macbook/macbook-air-m2' },
        { label: 'Sell MacBook Pro 16"', link: '/sell-my/macbook/macbook-pro-16' },
        { label: 'Sell Dell XPS', link: '/sell/laptops-sell/dell' },
        { label: 'Sell HP Spectre', link: '/sell/laptops-sell/hp' },
        { label: 'Sell Lenovo ThinkPad', link: '/sell/laptops-sell/lenovo' },
      ],
    },
    {
      heading: 'Tablets & wearables we buy',
      items: [
        { label: 'Sell iPad Pro', link: '/sell-my/ipad/ipad-pro-13-m4' },
        { label: 'Sell iPad Air', link: '/sell-my/ipad/ipad-air-11-m2' },
        { label: 'Sell iPad Mini', link: '/sell-my/ipad/ipad-mini-7' },
        { label: 'Sell Samsung Galaxy Tab', link: '/sell/tablets-sell/samsung' },
        { label: 'Sell Apple Watch', link: '/sell/watches-sell/apple' },
      ],
    },
    {
      heading: 'Gaming consoles we buy',
      items: [
        { label: 'Sell PlayStation 5', link: '/sell/consoles-sell/sony' },
        { label: 'Sell Xbox Series X', link: '/sell/consoles-sell/microsoft' },
        { label: 'Sell Nintendo Switch OLED', link: '/sell/consoles-sell/nintendo' },
        { label: 'Sell Steam Deck', link: '/sell/consoles-sell/valve' },
      ],
    },
  ];
  return (
    <section className="bg-white py-12 sm:py-14 border-t border-slate-100" data-testid="most-popular-devices-we-buy">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Most popular devices we buy</h2>
        <p className="text-sm sm:text-base text-slate-600 mb-8 max-w-3xl">
          Click any model to get an instant quote in 30 seconds. We accept every device, every condition, every brand.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {groups.map((g, gi) => (
            <div key={gi} className="border-t border-slate-200 pt-6" data-testid={`popular-sell-group-${gi}`}>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-4">{g.heading}</h3>
              <ul className="space-y-2.5">
                {g.items.map((it, i) => (
                  <li key={i}>
                    <Link to={it.link} className="group inline-flex items-center gap-1.5 text-sm sm:text-base text-slate-700 hover:text-emerald-600 transition-colors">
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-emerald-500" />
                      <span className="underline-offset-2 group-hover:underline">{it.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Comparison table, 2A vs competitors (live 2A pricing) ───────────────────
const COMPETITOR_RATES = [
  { name: 'Apple Trade-In', rate: 0.77, payment: 'Apple Store credit only', speed: '5-7 days' },
  { name: 'Gazelle', rate: 0.79, payment: 'Check or Amazon credit', speed: '5-10 days' },
  { name: 'Decluttr', rate: 0.70, payment: 'Check or PayPal', speed: '5-10 days' },
  { name: 'BestBuy Trade-In', rate: 0.66, payment: 'BestBuy gift card only', speed: '7+ days' },
];

export const SellVsCompetitorsTable = ({ modelSlug = 'iphone-15-pro', modelName = 'iPhone 15 Pro', storage = '256GB' }) => {
  const [live, setLive] = useState(null); // { price, storage }

  // Pull the REAL instant-quote price from website_sell_prices so the table
  // always matches what the estimate tool actually offers.
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prices/sells?model=${encodeURIComponent(modelSlug)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d?.prices?.length) return;
        const flawless = d.prices.filter(p => p.condition === 'flawless' && p.price > 0);
        if (!flawless.length) return;
        const match = flawless.find(p => p.storage === storage) || flawless[0];
        setLive({ price: Math.round(match.price), storage: match.storage });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [modelSlug, storage]);

  // Fallback keeps the section rendering even if pricing is unavailable
  const base = live?.price || 700;
  const usedStorage = live?.storage || storage;
  const round5 = (n) => Math.max(5, Math.round(n / 5) * 5);
  const rows = [
    { name: '2A Electronics', price: `$${base}`, payment: 'Cash, Zelle, Venmo, PayPal', speed: 'Same day', highlight: true },
    ...COMPETITOR_RATES.map(c => ({ name: c.name, price: `$${round5(base * c.rate)}`, payment: c.payment, speed: c.speed })),
  ];
  const asOf = live
    ? new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'February 2026';
  const art = /^[aeiou]/i.test(modelName) ? 'an' : 'a';

  return (
    <section className="bg-slate-50 border-y border-slate-200 py-12 sm:py-14" data-testid="sell-vs-competitors">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">2A vs the big buyback sites</h2>
        <p className="text-sm sm:text-base text-slate-600 mb-8 text-center max-w-2xl mx-auto">
          {live
            ? `Our live instant-quote price for ${art} ${modelName} ${usedStorage}, like-new condition, vs typical competitor trade-in values.`
            : `Real-world quotes for ${art} ${modelName} ${usedStorage}, like-new condition.`}
        </p>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Buyer</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Quoted price</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Payment</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Speed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={`border-t border-slate-100 ${row.highlight ? 'bg-emerald-50' : ''}`}>
                  <td className={`px-4 py-3 font-${row.highlight ? 'bold' : 'medium'} text-slate-${row.highlight ? '900' : '800'}`}>
                    {row.highlight && <Award className="w-4 h-4 inline mr-1.5 text-emerald-600" />}
                    {row.name}
                  </td>
                  <td className={`px-4 py-3 ${row.highlight ? 'font-bold text-emerald-600' : 'text-slate-700'}`} data-testid={row.highlight ? 'sell-vs-2a-price' : undefined}>{row.price}</td>
                  <td className="px-4 py-3 text-slate-600">{row.payment}</td>
                  <td className="px-4 py-3 text-slate-600">{row.speed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 text-center mt-4">
          {live
            ? `2A price is our live instant-quote price for ${art} ${modelName} ${usedStorage} unlocked in like-new condition (${asOf}). Competitor figures are typical published trade-in values and may vary by device and condition.`
            : `Quotes pulled ${asOf} for ${art} ${modelName} ${usedStorage} unlocked in like-new condition. Your quote may vary by device and condition.`}
        </p>
      </div>
    </section>
  );
};

// ── Extended FAQs for sell pages (12 Q&As) ───────────────────────────────────
const SELL_FAQS = (brand) => [
  {
    q: `How much can I get for my ${brand || 'phone'} at 2A Electronics?`,
    a: `Pricing depends on model, storage, and condition. Like-new ${brand || 'flagship'} phones can fetch $300-$950. Use our instant quote tool, we'll give you an exact number in 30 seconds, with no obligation.`,
  },
  {
    q: 'Do you pay more than Apple Trade-In or Gazelle?',
    a: 'Yes, typically more. We pay cash (not store credit like Apple) and process payment same-day. Compare quotes side-by-side using our instant tool before you decide.',
  },
  {
    q: 'How do I get paid?',
    a: 'For walk-in customers: cash, Zelle, or Venmo on the spot. For mail-in: payment is processed the same day your device arrives at our shop. We also support PayPal and bank transfer.',
  },
  {
    q: 'Is shipping really free?',
    a: 'Yes. We provide a prepaid UPS or USPS label insured up to $1,000. Print at home or drop off at any UPS/USPS location, no out-of-pocket cost to you.',
  },
  {
    q: 'What condition should my device be in to sell?',
    a: 'We buy devices in good working condition that power on and function normally. Quotes are adjusted based on the model and overall condition, and you get cash on the spot.',
  },
  {
    q: 'Can I cancel my quote and get my device back?',
    a: 'Absolutely. If you reject our final offer after inspection, we ship your device back to you free of charge, fully insured. There\'s zero risk in getting a quote.',
  },
  {
    q: 'What do I need to do before sending my device?',
    a: 'For Apple devices, sign out of iCloud and turn off Find My iPhone. For all devices, factory reset and remove any SIM cards or memory cards. If you forget, we\'ll guide you through it remotely.',
  },
  {
    q: 'Do you buy phones still on a payment plan or financing?',
    a: 'Usually no, phones must be fully paid off and unlocked (or have removable carrier balance). If unsure, send us your IMEI and we\'ll check status before issuing a quote.',
  },
  {
    q: 'How fast can I get cash for my device in Chicago?',
    a: `Walk into our shop at ${BUSINESS.address} and you'll walk out with cash in 15 minutes. Mail-in usually completes in 2-4 business days from when you ship it.`,
  },
  {
    q: 'Do you buy multiple devices at once?',
    a: 'Yes, we love bulk deals. Bring or ship 5+ devices and we\'ll give you a single combined quote, often with a small volume bonus. For 10+ devices, contact us for a custom corporate quote.',
  },
  {
    q: 'Is 2A Electronics legit and trusted?',
    a: 'Yes, we\'ve been Chicago\'s top-rated buyer, with 369+ verified 5-star Google reviews and thousands of successful purchases. We\'re an Apple Independent Repair Provider and serve corporate IT teams across the city.',
  },
  {
    q: 'What happens to my data when I sell?',
    a: 'After your factory reset, we run an additional certified wipe before re-sale. Your data is gone for good. We never recover, retain, or transmit any data from sold devices.',
  },
];

export const SellExtendedFAQs = ({ baseFaqs = [], brand }) => {
  const existing = new Set(baseFaqs.map(f => f.q.toLowerCase()));
  const merged = [...baseFaqs];
  for (const f of SELL_FAQS(brand)) {
    if (!existing.has(f.q.toLowerCase())) {
      merged.push(f);
      existing.add(f.q.toLowerCase());
    }
    if (merged.length >= 12) break;
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": merged.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  return (
    <section className="bg-slate-50 border-y border-slate-200 py-12 sm:py-14" data-testid="sell-extended-faqs">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Selling your device, your questions answered</h2>
        <p className="text-sm sm:text-base text-slate-600 mb-8 max-w-2xl">
          Real answers to what sellers actually ask us. Don't see your question? Call (773) 766-5814.
        </p>
        <div className="space-y-3">
          {merged.map((faq, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-emerald-200 transition-colors" data-testid={`sell-faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer px-5 py-4 text-base sm:text-lg font-semibold text-slate-900 list-none">
                <span>{faq.q}</span>
                <ChevronRight className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0 ml-3" />
              </summary>
              <div className="px-5 pb-5 text-sm sm:text-base text-slate-700 leading-relaxed">{faq.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Driving directions (same store) ──────────────────────────────────────────
export const SellDrivingDirections = ({ locality = 'Chicago' }) => (
  <section className="bg-white border-t border-slate-100 py-12 sm:py-14" data-testid="sell-driving-directions">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">How to find us from {locality}</h2>
      <p className="text-sm sm:text-base text-slate-600 mb-8 max-w-3xl">
        We're located at <strong>{BUSINESS.address}</strong>, right on Irving Park Road on the Northwest Side.
        Free street parking, walk-ins welcome, no appointment needed to sell.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { title: 'From downtown Chicago', text: 'I-90/94 W (Kennedy Expressway), exit Fullerton Ave, head west, right on Pulaski, north to Irving Park Rd, west to our shop. ~25 minutes.' },
          { title: 'From north suburbs', text: 'I-94 E to Addison St exit, right on Addison, sharp left onto Avondale, right on Irving Park Rd. ~20 minutes from Evanston.' },
          { title: 'CTA / public transit', text: 'Blue Line to Irving Park station, transfer to #80 Irving Park bus going west. ~12 minutes from the station.' },
          { title: 'From OHare / west suburbs', text: 'I-90 E (Kennedy), exit Cumberland Ave, east on Irving Park Rd. ~15 minutes from OHare.' },
        ].map((d, i) => (
          <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-6" data-testid={`sell-direction-${i}`}>
            <div className="flex items-center gap-2 mb-3">
              <Navigation className="w-5 h-5 text-emerald-500" />
              <h3 className="font-bold text-slate-900">{d.title}</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{d.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ── Nearby neighborhoods (sell-flavored) ─────────────────────────────────────
export const SellNearbyNeighborhoods = () => (
  <section className="bg-slate-50 border-y border-slate-200 py-12 sm:py-14" data-testid="sell-nearby-neighborhoods">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Chicago neighborhoods we buy from</h2>
      <p className="text-sm sm:text-base text-slate-600 mb-8 max-w-2xl">
        We're the closest professional buyer for sellers across the Northwest Side and beyond.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {NEIGHBORHOODS_FOR_SELL.map((n, i) => (
          <Link
            key={i}
            to={`/sell-near/${n.slug}`}
            className="group flex items-center justify-between p-4 bg-white hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 rounded-xl transition-all"
            data-testid={`sell-near-${n.slug}`}
          >
            <div>
              <p className="font-semibold text-slate-900 group-hover:text-emerald-700">Sell in {n.name}</p>
              <p className="text-xs text-slate-500">{n.miles} mi from our shop</p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-500" />
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// ── Localized sell content (3 paragraphs) ────────────────────────────────────
export const SellLocalizedContent = ({ locality = 'Chicago' }) => (
  <section className="bg-white py-12 sm:py-14 border-t border-slate-100" data-testid="sell-localized-content">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-10">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Sell your phone, laptop, or tablet in {locality}</h2>
        <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
          Have an old iPhone gathering dust in a drawer? A MacBook that's been replaced? A broken Galaxy you assumed was worthless? Bring it
          to 2A Electronics in {locality}, we'll turn it into cash, today. We buy every model, every brand, every condition. From flagship
          iPhones to last year's Galaxy, from like-new MacBooks to water-damaged Pixels, there's value sitting in your drawer, and we'll pay
          you for it in 15 minutes.
        </p>
      </div>
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Why sell to 2A instead of Apple Trade-In, Gazelle, or BestBuy?</h2>
        <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
          Three reasons most sellers in {locality} skip the big-box chains and walk into our shop instead. <strong>Higher payouts:</strong> we
          typically pay more than the national buyback sites because we don't have their overhead. <strong>Real cash, same day:</strong>
          no waiting 7-10 days for a check or being locked into store credit. <strong>Local accountability:</strong> we're a brick-and-mortar
          Chicago shop with 470 verified Google reviews, not a faceless processing center in another state.
        </p>
      </div>
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Selling broken devices in {locality}</h2>
        <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
          Cracked screen? Water damage? Won't turn on? Stuck on the Apple logo? You probably assumed your device is worthless. It's not. As an
          Apple Independent Repair Provider, we recover usable parts, logic boards, cameras, batteries, screens, from every broken device
          we buy. That means we pay more for broken devices than competitors who simply scrap them. Bring it in or mail it for free, let's
          find out what your "broken" device is actually worth.
        </p>
      </div>
    </div>
  </section>
);

// ── Master block: everything in one composable component ─────────────────────
export const SellArchitectureBlock = ({ locality = 'Chicago', brand = '', baseFaqs = [] }) => (
  <>
    <SellStoreInfoBar />
    <SellFulfillmentCards />
    <WhatToExpectSell />
    <BrandsWeBuyStrip />
    <ConditionsWeBuy />
    <SellVsCompetitorsTable />
    <MostPopularDevicesWeBuy />
    <SellExtendedFAQs baseFaqs={baseFaqs} brand={brand} />
    <SellDrivingDirections locality={locality} />
    <SellLocalizedContent locality={locality} />
    <SellNearbyNeighborhoods />
  </>
);

export default SellArchitectureBlock;
